import React from "react";
import { Box, Button, Skeleton, Typography } from "@mui/material";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { C } from "../common/theme";

/** Nothing to show: an icon, a line, optional detail and action. */
export function EmptyState({ title, body, action, icon }: { title: React.ReactNode; body?: React.ReactNode; action?: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <Box sx={{ py: 5, px: 2, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
      <Box sx={{ color: C.faint, "& svg": { fontSize: 30 } }}>{icon ?? <InboxOutlinedIcon />}</Box>
      <Typography variant="subtitle1">{title}</Typography>
      {body && (
        <Typography variant="body2" sx={{ color: C.muted, maxWidth: 420 }}>
          {body}
        </Typography>
      )}
      {action && <Box sx={{ mt: 0.5 }}>{action}</Box>}
    </Box>
  );
}

/** A load failed: the message and a Try again button. */
export function ErrorState({ message, onRetry }: { message?: React.ReactNode; onRetry?: () => void }) {
  return (
    <Box role="alert" sx={{ py: 4, px: 2, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
      <Box sx={{ color: C.danger, "& svg": { fontSize: 28 } }}>
        <ErrorOutlineIcon />
      </Box>
      <Typography variant="subtitle1">{message ?? "Couldn't load this."}</Typography>
      {onRetry && (
        <Button variant="outlined" size="small" onClick={onRetry}>
          Try again
        </Button>
      )}
    </Box>
  );
}

/** Placeholder rows while a list loads. */
export function LoadingRows({ rows = 5, height = 44 }: { rows?: number; height?: number }) {
  return (
    <Box aria-busy="true" aria-label="Loading" sx={{ display: "flex", flexDirection: "column", gap: 0.75, p: 1.5 }}>
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} variant="rounded" height={height} sx={{ borderRadius: "6px" }} />
      ))}
    </Box>
  );
}
